import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
  Chip,
  Alert,
  InputAdornment,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import JsBarcode from "jsbarcode";
import Grid from "./utils/CompatGrid";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PrintIcon from "@mui/icons-material/Print";
import ScaleIcon from "@mui/icons-material/Scale";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import UsbIcon from "@mui/icons-material/Usb";
import BluetoothIcon from "@mui/icons-material/Bluetooth";
import AddIcon from "@mui/icons-material/Add";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  exportSingleLabelPdf,
  exportCopiesAsA4Pdf,
  printLabelNode,
  printCopiesOnA4,
  computeA4Grid,
} from "./utils/pdfExport";
import { mmToPx, LABEL_SIZE_PRESETS } from "./utils/labelSizes";
import {
  connectScale,
  isSerialSupported,
  listPairedPorts,
  describePort,
  requestNewPort,
} from "./utils/serialScale";
import {
  connectBluetoothScale,
  isBluetoothSupported,
} from "./utils/bluetoothScale";

// Preset weight options (grams) for manual entry when no scale is connected.
const WEIGHT_PRESETS = [
  50, 100, 150, 200, 250, 300, 500, 750, 1000, 1500, 2000,
];

const STICKER_SIZES = LABEL_SIZE_PRESETS.filter((p) =>
  ["40x20", "50x30", "60x40", "thermalroll"].includes(p.key),
);

const formatGrams = (grams) => {
  const g = Number(grams) || 0;
  return g >= 1000 ? `${(g / 1000).toFixed(3)} kg` : `${Math.round(g)} g`;
};

// Simple, self-contained weight sticker: no product/template selection.
// Weight comes live from a connected scale, or is entered manually (with
// quick-pick presets); price, MFG/EXP dates and batch number are typed in
// by the shopkeeper, then the sticker is printed/exported directly.
const WeightSticker = () => {
  const [itemName, setItemName] = useState("");
  const [sizeKey, setSizeKey] = useState("50x30");

  const [scaleConnection, setScaleConnection] = useState(null);
  const [scaleStatus, setScaleStatus] = useState("disconnected"); // disconnected | connecting | connected
  const [scaleMenuAnchor, setScaleMenuAnchor] = useState(null);
  const [pairedPorts, setPairedPorts] = useState([]);
  const [baudRate, setBaudRate] = useState(9600);
  const [weightGrams, setWeightGrams] = useState("");
  const [weightPreset, setWeightPreset] = useState("");

  const [price, setPrice] = useState("");
  const [batchNumber, setBatchNumber] = useState("");
  const [manufacturingDate, setManufacturingDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [copies, setCopies] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [showBarcode, setShowBarcode] = useState(false);
  const [barcodeValue, setBarcodeValue] = useState("");

  const previewNodeRef = useRef(null);
  const barcodeRef = useRef(null);

  // Release the scale connection when the page unmounts.
  useEffect(() => {
    return () => {
      scaleConnection?.disconnect?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render barcode when it changes
  useEffect(() => {
    if (showBarcode && barcodeValue && barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, barcodeValue, {
          format: "CODE128",
          width: 2,
          height: 40,
          displayValue: true,
          fontSize: 10,
        });
      } catch (err) {
        console.error("Error generating barcode:", err);
      }
    }
  }, [showBarcode, barcodeValue]);

  const selectedSize = useMemo(
    () => STICKER_SIZES.find((s) => s.key === sizeKey) || STICKER_SIZES[0],
    [sizeKey],
  );

  const a4Grid = useMemo(
    () => computeA4Grid(selectedSize.widthMm, selectedSize.heightMm),
    [selectedSize],
  );

  const normalizeCopies = (value) => Math.max(1, Number(value) || 1);

  const serialSupported = isSerialSupported();
  const bluetoothSupported = isBluetoothSupported();
  const noScaleApiSupport = !serialSupported && !bluetoothSupported;

  // Opens the selected (or freshly-paired) serial port and starts streaming weight.
  const startConnection = async (port) => {
    setScaleMenuAnchor(null);
    setScaleStatus("connecting");
    try {
      const connection = await connectScale({
        port,
        baudRate,
        onWeight: (kg) => {
          setWeightPreset("");
          setWeightGrams(Math.round(kg * 1000).toString());
        },
        onError: () => {
          toast.error("Lost connection to the weighing scale");
          setScaleStatus("disconnected");
          setScaleConnection(null);
        },
      });
      setScaleConnection(connection);
      setScaleStatus("connected");
      toast.success("Scale connected");
    } catch (err) {
      setScaleStatus("disconnected");
      toast.error(err.message || "Failed to connect to scale");
    }
  };

  // Clicking the button while connected just disconnects; otherwise it opens
  // a menu listing already-paired serial devices plus options to pair new
  // serial or Bluetooth devices.
  const handleConnectScaleClick = async (e) => {
    if (scaleConnection) {
      await scaleConnection.disconnect();
      setScaleConnection(null);
      setScaleStatus("disconnected");
      return;
    }
    if (noScaleApiSupport) {
      toast.error(
        "This browser doesn't support Web Serial or Web Bluetooth. Use Chrome/Edge on a PC (over HTTPS or localhost), or enter weight manually.",
      );
      return;
    }
    const anchor = e.currentTarget;
    const ports = serialSupported
      ? await listPairedPorts().catch(() => [])
      : [];
    setPairedPorts(ports);
    setScaleMenuAnchor(anchor);
  };

  const handlePickNewDevice = async () => {
    setScaleMenuAnchor(null);
    try {
      const port = await requestNewPort();
      await startConnection(port);
    } catch (err) {
      // User closed the device chooser without selecting anything.
      if (err?.name !== "NotFoundError") {
        toast.error(err.message || "Failed to open device chooser");
      }
    }
  };

  const handlePickBluetoothDevice = async (showAllDevices = false) => {
    setScaleMenuAnchor(null);
    setScaleStatus("connecting");
    try {
      const connection = await connectBluetoothScale({
        showAllDevices,
        onWeight: (kg) => {
          setWeightPreset("");
          setWeightGrams(Math.round(kg * 1000).toString());
        },
        onError: () => {
          toast.error("Lost connection to the Bluetooth scale");
          setScaleStatus("disconnected");
          setScaleConnection(null);
        },
      });
      setScaleConnection(connection);
      setScaleStatus("connected");
      toast.success("Bluetooth scale connected");
    } catch (err) {
      setScaleStatus("disconnected");
      if (err?.name === "NotFoundError" && !showAllDevices) {
        // No BLE device advertising the Weight Scale service was nearby -
        // most low-cost scales use classic Bluetooth (SPP), which the
        // picker can't show at all; only offer the "show all" fallback in
        // case the scale is BLE but just isn't advertising the service.
        toast.info(
          "No BLE weighing scale found nearby. If your scale uses classic Bluetooth (most do), pair it in your OS Bluetooth settings and connect via 'Pair New USB/Serial Device' instead.",
        );
      } else if (err?.name !== "NotFoundError") {
        toast.error(err.message || "Failed to connect to Bluetooth scale");
      }
    }
  };

  const handlePresetChange = (value) => {
    setWeightPreset(value);
    if (value) setWeightGrams(value);
  };

  const canGenerate = weightGrams && price;

  const handleExportPdf = async () => {
    if (!canGenerate) {
      toast.error("Enter weight and price first");
      return;
    }
    const safeCopies = normalizeCopies(copies);
    setCopies(safeCopies);
    setGenerating(true);
    try {
      if (safeCopies > 1) {
        await exportCopiesAsA4Pdf(
          previewNodeRef.current,
          selectedSize.widthMm,
          selectedSize.heightMm,
          safeCopies,
          {},
          `${itemName || "sticker"}.pdf`,
        );
      } else {
        await exportSingleLabelPdf(
          previewNodeRef.current,
          selectedSize.widthMm,
          selectedSize.heightMm,
          `${itemName || "sticker"}.pdf`,
        );
      }
    } catch (err) {
      toast.error("Failed to export PDF");
    } finally {
      setGenerating(false);
    }
  };

  const handlePrint = async () => {
    if (!canGenerate) {
      toast.error("Enter weight and price first");
      return;
    }
    const safeCopies = normalizeCopies(copies);
    setCopies(safeCopies);
    setGenerating(true);
    try {
      if (safeCopies > 1) {
        await printCopiesOnA4(
          previewNodeRef.current,
          selectedSize.widthMm,
          selectedSize.heightMm,
          safeCopies,
        );
      } else {
        await printLabelNode(
          previewNodeRef.current,
          selectedSize.widthMm,
          selectedSize.heightMm,
        );
      }
    } catch (err) {
      toast.error("Failed to print");
    } finally {
      setGenerating(false);
    }
  };

  const widthPx = mmToPx(selectedSize.widthMm) * 2;
  const heightPx = mmToPx(selectedSize.heightMm) * 2;

  return (
    <Box>
      <ToastContainer position="top-right" autoClose={3500} hideProgressBar />
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        Weight Sticker (Live Scale)
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 3 }}>
            <TextField
              label="Item Name"
              size="small"
              fullWidth
              sx={{ mb: 2 }}
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
            />

            <TextField
              select
              label="Sticker Size"
              size="small"
              fullWidth
              sx={{ mb: 2 }}
              value={sizeKey}
              onChange={(e) => setSizeKey(e.target.value)}
            >
              {STICKER_SIZES.map((s) => (
                <MenuItem key={s.key} value={s.key}>
                  {s.label}
                </MenuItem>
              ))}
            </TextField>

            <Divider sx={{ my: 2 }} />

            {serialSupported && (
              <TextField
                select
                label="Serial Baud Rate"
                size="small"
                fullWidth
                sx={{ mb: 2 }}
                disabled={!!scaleConnection}
                value={baudRate}
                onChange={(e) => setBaudRate(Number(e.target.value))}
                helperText="Match your scale's indicator setting (check its manual if no data appears)"
              >
                {[1200, 2400, 4800, 9600, 19200, 38400].map((rate) => (
                  <MenuItem key={rate} value={rate}>
                    {rate}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Button
                variant={scaleConnection ? "outlined" : "contained"}
                color={scaleConnection ? "error" : "primary"}
                startIcon={scaleConnection ? <LinkOffIcon /> : <ScaleIcon />}
                onClick={handleConnectScaleClick}
                size="small"
              >
                {scaleConnection ? "Disconnect Scale" : "Connect Scale"}
              </Button>
              <Chip
                size="small"
                label={scaleStatus}
                color={
                  scaleStatus === "connected"
                    ? "success"
                    : scaleStatus === "connecting"
                      ? "warning"
                      : "default"
                }
              />
              <Menu
                anchorEl={scaleMenuAnchor}
                open={Boolean(scaleMenuAnchor)}
                onClose={() => setScaleMenuAnchor(null)}
              >
                {serialSupported && pairedPorts.length === 0 && (
                  <MenuItem disabled>No paired USB/serial devices yet</MenuItem>
                )}
                {pairedPorts.map((port, idx) => (
                  <MenuItem key={idx} onClick={() => startConnection(port)}>
                    <ListItemIcon>
                      <UsbIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{describePort(port, idx)}</ListItemText>
                  </MenuItem>
                ))}
                {serialSupported && (
                  <MenuItem onClick={handlePickNewDevice}>
                    <ListItemIcon>
                      <AddIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Pair New USB/Serial Device...</ListItemText>
                  </MenuItem>
                )}
                {serialSupported && bluetoothSupported && <Divider />}
                {bluetoothSupported && (
                  <MenuItem onClick={() => handlePickBluetoothDevice(false)}>
                    <ListItemIcon>
                      <BluetoothIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Scan for BLE Weighing Scale..."
                      secondary="Only detects Bluetooth Low Energy scales"
                    />
                  </MenuItem>
                )}
                {bluetoothSupported && (
                  <MenuItem onClick={() => handlePickBluetoothDevice(true)}>
                    <ListItemIcon>
                      <BluetoothIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Show All Bluetooth Devices..."
                      secondary="Use if your scale isn't found above"
                    />
                  </MenuItem>
                )}
              </Menu>
            </Box>

            {noScaleApiSupport && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This browser/connection doesn't support Web Serial or Web
                Bluetooth (needs Chrome/Edge over HTTPS or localhost). Use the
                Weight fields below to enter the reading manually.
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Quick-pick Weight"
                  size="small"
                  fullWidth
                  disabled={!!scaleConnection}
                  value={weightPreset}
                  onChange={(e) => handlePresetChange(e.target.value)}
                >
                  <MenuItem value="">Custom</MenuItem>
                  {WEIGHT_PRESETS.map((g) => (
                    <MenuItem key={g} value={g}>
                      {formatGrams(g)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Weight"
                  type="number"
                  size="small"
                  fullWidth
                  disabled={!!scaleConnection}
                  value={weightGrams}
                  onChange={(e) => {
                    setWeightPreset("");
                    setWeightGrams(e.target.value);
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">g</InputAdornment>
                    ),
                  }}
                  helperText={
                    scaleConnection ? "Live from scale" : "Manual entry"
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Price"
                  type="number"
                  size="small"
                  fullWidth
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Batch Number"
                  size="small"
                  fullWidth
                  value={batchNumber}
                  onChange={(e) => setBatchNumber(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Barcode"
                  size="small"
                  fullWidth
                  value={barcodeValue}
                  onChange={(e) => setBarcodeValue(e.target.value)}
                  placeholder="Enter barcode value (optional)"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showBarcode}
                      onChange={(e) => setShowBarcode(e.target.checked)}
                    />
                  }
                  label="Show Barcode on Sticker"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Copies"
                  type="number"
                  size="small"
                  fullWidth
                  value={copies}
                  inputProps={{ min: 1 }}
                  onChange={(e) => setCopies(normalizeCopies(e.target.value))}
                  helperText={
                    sizeKey === "a4sheet"
                      ? "Total copies; remaining stickers continue on next A4 pages"
                      : `${a4Grid.perPage} fit on one A4 page (${a4Grid.columns} x ${a4Grid.rows} grid); rest continue on next pages`
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="MFG Date"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={manufacturingDate}
                  onChange={(e) => setManufacturingDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="EXP Date"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                />
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                disabled={!canGenerate || generating}
                onClick={handleExportPdf}
                fullWidth
              >
                Generate PDF
              </Button>
              <Button
                variant="contained"
                startIcon={<PrintIcon />}
                disabled={!canGenerate || generating}
                onClick={handlePrint}
                fullWidth
              >
                Print
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper
            elevation={1}
            sx={{
              p: 3,
              borderRadius: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              Preview
            </Typography>
            <div
              ref={previewNodeRef}
              style={{
                width: widthPx,
                height: heightPx,
                background: "#fff",
                border: "1px solid #d0d7de",
                boxShadow: "0 0 0 1px #ddd",
                boxSizing: "border-box",
                padding: 8,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: 2,
                fontFamily: "Arial, sans-serif",
                overflow: "hidden",
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 14 }}>
                {itemName || "Item Name"}{" "}
                {weightGrams ? formatGrams(weightGrams) : ""}
              </div>
              <div style={{ fontWeight: 700, fontSize: 13 }}>
                Price : {price ? `₹${price}` : "--"}
              </div>
              <div style={{ fontSize: 11 }}>
                MFG. Date : {manufacturingDate || "--"}
              </div>
              <div style={{ fontSize: 11 }}>
                EXP. Date : {expiryDate || "--"}
              </div>
              <div style={{ fontSize: 11 }}>
                Batch No : {batchNumber || "--"}
              </div>
              {showBarcode && barcodeValue && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: 4,
                  }}
                >
                  <svg ref={barcodeRef}></svg>
                </div>
              )}
            </div>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WeightSticker;
